import type { ReadingGoalsDashboard, ReadingTimeSegment } from "./types";

export interface ReadingGoalRange {
  from: string;
  to: string;
}

export interface ReadingGoalRanges {
  day: ReadingGoalRange;
  week: ReadingGoalRange;
}

export interface ReadingGoalProgress {
  ratio: number;
  fill: number;
  complete: boolean;
  remainingMs: number;
}

export type ReadingGoalKind = "daily" | "weekly";

export interface ReadingGoalCelebration {
  kind: ReadingGoalKind;
  periodFrom: string;
  targetMinutes: number;
}

export interface ReadingGoalSegmentUpdate {
  dashboard: ReadingGoalsDashboard;
  celebrations: ReadingGoalCelebration[];
}

/** Local calendar boundaries keep goals aligned with the reader's actual day, including DST. */
export function currentReadingGoalRanges(now = new Date()): ReadingGoalRanges {
  if (!Number.isFinite(now.getTime())) throw new RangeError("Reading goals need a valid date");

  const year = now.getFullYear();
  const month = now.getMonth();
  const date = now.getDate();
  const dayStart = new Date(year, month, date);
  const dayEnd = new Date(year, month, date + 1);
  const daysSinceMonday = (dayStart.getDay() + 6) % 7;
  const weekStart = new Date(year, month, date - daysSinceMonday);
  const weekEnd = new Date(year, month, date - daysSinceMonday + 7);

  return {
    day: { from: dayStart.toISOString(), to: dayEnd.toISOString() },
    week: { from: weekStart.toISOString(), to: weekEnd.toISOString() }
  };
}

export function readingGoalProgress(totalMs: number, targetMinutes: number | null): ReadingGoalProgress | null {
  if (targetMinutes === null) return null;
  const targetMs = targetMinutes * 60_000;
  const elapsedMs = Math.max(0, totalMs);
  const ratio = elapsedMs / targetMs;
  return {
    ratio,
    fill: Math.min(1, ratio),
    complete: elapsedMs >= targetMs,
    remainingMs: Math.max(0, targetMs - elapsedMs)
  };
}

function intervalOverlapMs(
  range: Pick<ReadingGoalsDashboard["progress"]["day"], "from" | "to">,
  segment: Pick<ReadingTimeSegment, "startedAt" | "endedAt">
): number {
  const rangeStart = Date.parse(range.from);
  const rangeEnd = Date.parse(range.to);
  const segmentStart = Date.parse(segment.startedAt);
  const segmentEnd = Date.parse(segment.endedAt);
  if (![rangeStart, rangeEnd, segmentStart, segmentEnd].every(Number.isFinite)) return 0;
  return Math.max(0, Math.min(rangeEnd, segmentEnd) - Math.max(rangeStart, segmentStart));
}

/**
 * Applies a newly persisted reading interval to the open reader's goal totals.
 * A celebration is emitted only for the exact transition from incomplete to complete.
 */
export function applyReadingTimeToGoals(
  dashboard: ReadingGoalsDashboard,
  segment: ReadingTimeSegment
): ReadingGoalSegmentUpdate {
  const nextProgress = {
    day: {
      ...dashboard.progress.day,
      totalMs: dashboard.progress.day.totalMs + intervalOverlapMs(dashboard.progress.day, segment)
    },
    week: {
      ...dashboard.progress.week,
      totalMs: dashboard.progress.week.totalMs + intervalOverlapMs(dashboard.progress.week, segment)
    }
  };
  const celebrations: ReadingGoalCelebration[] = [];
  const candidates = [
    {
      kind: "daily" as const,
      before: dashboard.progress.day,
      after: nextProgress.day,
      targetMinutes: dashboard.goals.dailyMinutes
    },
    {
      kind: "weekly" as const,
      before: dashboard.progress.week,
      after: nextProgress.week,
      targetMinutes: dashboard.goals.weeklyMinutes
    }
  ];

  for (const candidate of candidates) {
    const before = readingGoalProgress(candidate.before.totalMs, candidate.targetMinutes);
    const after = readingGoalProgress(candidate.after.totalMs, candidate.targetMinutes);
    if (candidate.targetMinutes !== null && before && after && !before.complete && after.complete) {
      celebrations.push({
        kind: candidate.kind,
        periodFrom: candidate.after.from,
        targetMinutes: candidate.targetMinutes
      });
    }
  }

  return {
    dashboard: { ...dashboard, progress: nextProgress },
    celebrations
  };
}
